import { Canvas } from '@react-three/fiber';
import styled from 'styled-components';
import type { ShaderCanvasProps } from './types';
import { getSafeDPRRange } from '../../lib/deviceUtils';

const CanvasWrapper = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    
    canvas {
        display: block;
        width: 100%;
        height: 100%;
    }
`;

/**
 * ShaderCanvas - Wrapper component that sets up the Three.js scene for shaders
 * 
 * @example
 * ```tsx
 * <ShaderCanvas style={{ width: '100vw', height: '100vh' }}>
 *   <FragmentShader fragmentShader={myShader} />
 * </ShaderCanvas>
 * ```
 */
export default function ShaderCanvas({ 
    children, 
    className, 
    style 
}: ShaderCanvasProps) {
    const safeDPR = getSafeDPRRange();
    
    return (
        <CanvasWrapper className={className} style={style}>
            <Canvas
                frameloop="always"
                dpr={safeDPR}
                orthographic
                camera={{ 
                    position: [0, 0, 5],
                    zoom: 1,
                    near: 0.1,
                    far: 1000,
                    left: -1,
                    right: 1,
                    top: 1,
                    bottom: -1,
                }}
                gl={{ 
                    antialias: true,
                    alpha: true,
                    preserveDrawingBuffer: false,
                    powerPreference: 'high-performance',
                }}
                onCreated={({ gl }) => {
                    // Handle WebGL context loss
                    const canvas = gl.domElement;
                    canvas.addEventListener('webglcontextlost', (e) => {
                        e.preventDefault();
                        console.warn('WebGL context lost');
                    });
                    canvas.addEventListener('webglcontextrestored', () => {
                        console.info('WebGL context restored');
                    });
                }}
            >
                {children}
            </Canvas>
        </CanvasWrapper>
    );
}

